import { createTheme, PaletteColor, Theme, ThemeProvider, useTheme } from "@mui/material";
import { ReactNode } from "react";

const getMacros = (theme: Theme) => {
  const {
    palette: {
      accents: { green, blueDark, orangeDark },
    },
  } = theme;

  const cho: PaletteColor = {
    lighter: green[50],
    light: green[100],
    main: green[700],
    dark: green[900],
    contrastText: "#fff",
  };

  const pro: PaletteColor = {
    lighter: blueDark[50],
    light: blueDark[100],
    main: blueDark[700],
    dark: blueDark[900],
    contrastText: "#fff",
  };

  const fat: PaletteColor = {
    lighter: orangeDark[50],
    light: orangeDark[100],
    main: orangeDark[700],
    dark: orangeDark[900],
    contrastText: "#fff",
  };

  return { cho, pro, fat };
};

const getLikeDislike = (theme: Theme) => {
  const {
    palette: { success, error },
  } = theme;

  const like = success;
  const dislike = error;

  return { like, dislike };
};

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const {
    palette: { greyscale, success, warning, info, error },
  } = theme;
  const teamworksCalendarEvent = greyscale[300];
  const rpeLight = success;
  const rpeModerate = warning;
  const rpeHeavy = error;
  const orderStatusNew = info;
  const orderStatusCooking = warning;
  const orderStatusDone = success;
  const orderStatusCancelled = error;

  const { cho, pro, fat } = getMacros(theme);
  const { like, dislike } = getLikeDislike(theme);

  const nutritionPalette = {
    cho,
    pro,
    fat,
    like,
    dislike,
    teamworksCalendarEvent,
    rpeLight,
    rpeModerate,
    rpeHeavy,
    orderStatusNew,
    orderStatusCooking,
    orderStatusDone,
    orderStatusCancelled,
  };

  const extendedTheme = createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      ...nutritionPalette,
    },
    components: {
      ...theme.components,
      // @teamworksdev/mui's own default of variant="contained" (the standard black
      // button, white text) stopped reaching the final merged theme after bumping
      // @teamworksdev/mui + @mui/material together - restore it here so every
      // <Button> across the app that doesn't pass an explicit variant keeps its intended look.
      MuiButton: {
        ...theme.components?.MuiButton,
        defaultProps: {
          ...theme.components?.MuiButton?.defaultProps,
          variant: "contained",
        },
      },
      // the separator under Tabs (Teams/Tags/
      // Transfers/Goals etc.) relies on this border-width/style pair from @teamworksdev/mui;
      // only the color half survived the theme merge, so the border became invisible (0px).
      MuiTabs: {
        ...theme.components?.MuiTabs,
        styleOverrides: {
          ...theme.components?.MuiTabs?.styleOverrides,
          root: {
            ...(theme.components?.MuiTabs?.styleOverrides?.root as object),
            borderBottomWidth: "2px",
            borderBottomStyle: "solid",
          },
        },
      },
      // labels should always sit above their field
      // (shrink: true), not start as inline placeholder text that animates up on focus.
      MuiInputLabel: {
        ...theme.components?.MuiInputLabel,
        defaultProps: {
          ...theme.components?.MuiInputLabel?.defaultProps,
          shrink: true,
        },
      },
      // MUI's outlined border normally carves a gap ("notch") sized to the label text, meant
      // to be filled by the label sitting in it. Since labels here always render above the
      // field (shrink: true) instead, that notch is never filled and just shows as a blank
      // segment of the border. notched: false stops the gap from being carved out at all.
      MuiOutlinedInput: {
        ...theme.components?.MuiOutlinedInput,
        defaultProps: {
          ...theme.components?.MuiOutlinedInput?.defaultProps,
          notched: false,
        },
      },
      // without this, MuiDialogTitle falls back to plain
      // Typography h6 sizing (24px/700 here) instead of the design system's own dialog-title
      // size/weight.
      MuiDialogTitle: {
        ...theme.components?.MuiDialogTitle,
        defaultProps: {
          ...theme.components?.MuiDialogTitle?.defaultProps,
          component: "h3",
        },
        styleOverrides: {
          ...theme.components?.MuiDialogTitle?.styleOverrides,
          root: {
            ...(theme.components?.MuiDialogTitle?.styleOverrides?.root as object),
            fontSize: 20,
            fontWeight: 600,
            lineHeight: "28px",
          },
        },
      },
      // MUI's DialogActions has an 8px padding baked into its
      // own base styles (unaffected by any of this); this 16px margin stacks on top of that to
      // produce the intended 24px of space around the button row. The 8px gap BETWEEN buttons
      // comes from a separate MuiDialogActions-spacing rule, untouched by this override.
      MuiDialogActions: {
        ...theme.components?.MuiDialogActions,
        styleOverrides: {
          ...theme.components?.MuiDialogActions?.styleOverrides,
          root: {
            ...(theme.components?.MuiDialogActions?.styleOverrides?.root as object),
            justifyContent: "flex-end",
            margin: "16px",
          },
        },
      },
      // MUI's own default Tab minWidth (90px) forces every tab
      // to be at least that wide, so short labels (e.g. the Foods/Recipes/Branded selector) end
      // up centered in extra empty space that reads as inflated left-right padding. minWidth: 10
      // lets tabs size to their actual content instead.
      MuiTab: {
        ...theme.components?.MuiTab,
        styleOverrides: {
          ...theme.components?.MuiTab?.styleOverrides,
          root: {
            ...(theme.components?.MuiTab?.styleOverrides?.root as object),
            minWidth: 10,
            "&.Mui-selected": {
              fontWeight: 600,
              color: "rgba(0, 0, 0, 0.87)",
            },
            "&:hover": {
              color: "rgba(0, 0, 0, 0.87)",
            },
          },
        },
      },
      // this design system's own fontSize scale for icons
      // (e.g. large: 24px) stopped applying, so any icon using a named size like
      // fontSize="large" falls back to MUI's own native, much larger size (35px) instead.
      MuiSvgIcon: {
        ...theme.components?.MuiSvgIcon,
        variants: [
          ...(theme.components?.MuiSvgIcon?.variants ?? []),
          { props: { fontSize: "extraExtraExtraLarge" }, style: { fontSize: 48 } },
          { props: { fontSize: "extraExtraLarge" }, style: { fontSize: 40 } },
          { props: { fontSize: "extraLarge" }, style: { fontSize: 32 } },
          { props: { fontSize: "large" }, style: { fontSize: 24 } },
          { props: { fontSize: "medium" }, style: { fontSize: 20 } },
          { props: { fontSize: "small" }, style: { fontSize: 16 } },
          { props: { fontSize: "extraSmall" }, style: { fontSize: 12 } },
        ],
      },
    },
  });

  return <ThemeProvider theme={extendedTheme}>{children}</ThemeProvider>;
}
